import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { trackAPICall } from '../../utils/sentry';
import * as Sentry from '@sentry/react';
import { handleBusinessErrorIfApplicable } from '../../utils/businessErrorHandler';
import { handleAuthFailure, isPublicAuthPath } from '../../utils/authRedirect';

// Extend Axios config to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

const API_URL = import.meta.env.VITE_REACT_APP_API_URL || '';

// Function to create API client with the current id token and version header
export const createApiClient = (): AxiosInstance => {
  // Create axios instance with base configuration
  const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      'tm-front-version': '3'
    },
    timeout: 19000, // 19 second timeout
    withCredentials: false
  });
  
  // Add auth token interceptor that fetches fresh token on each request
  // This is the standard AWS Amplify v2 approach - fetchAuthSession handles token refresh automatically
  apiClient.interceptors.request.use(async (config) => {
    try {
      // fetchAuthSession automatically:
      // 1. Returns cached tokens if still valid
      // 2. Refreshes expired tokens automatically
      // 3. Handles all token lifecycle management
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();
      
      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (error) {
      console.warn('[apiClient] Failed to fetch auth session:', error);
      // Continue without auth header if session fetch fails
      // This allows public endpoints to work even when not authenticated
    }
    
    return config;
  });

  // Add request interceptor for Sentry tracking
  apiClient.interceptors.request.use((config) => {
    // Store start time for performance tracking
    (config as ExtendedAxiosRequestConfig).metadata = { startTime: Date.now() };
    return config;
  });

  // Add response interceptor for security and Sentry tracking
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Track successful API calls
      const config = response.config as ExtendedAxiosRequestConfig;
      const duration = config?.metadata?.startTime 
        ? Date.now() - config.metadata.startTime 
        : undefined;
      
      trackAPICall(
        response.config.url || 'unknown',
        response.config.method || 'GET',
        response.status,
        duration
      );
      
      return response;
    },
    (error) => {
      // Track failed API calls
      const config = error.config as ExtendedAxiosRequestConfig;
      const duration = config?.metadata?.startTime 
        ? Date.now() - config.metadata.startTime 
        : undefined;
      
      trackAPICall(
        error.config?.url || 'unknown',
        error.config?.method || 'GET',
        error.response?.status,
        duration
      );

      // Send detailed request information to Sentry for failed requests.
      // Skip expected auth failures (401/403) — they are not application errors.
      const status = error.response?.status;
      const isExpectedAuthFailure = status === 401 || status === 403;

      if (import.meta.env.VITE_NODE_ENV === 'production' && !isExpectedAuthFailure) {
        Sentry.withScope((scope) => {
          // Add request details
          scope.setContext('request', {
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            headers: error.config?.headers,
            data: error.config?.data,
            params: error.config?.params,
            timeout: error.config?.timeout,
          });

          // Add response details if available
          if (error.response) {
            scope.setContext('response', {
              status: error.response.status,
              statusText: error.response.statusText,
              headers: error.response.headers,
              data: error.response.data,
            });
          }

          // Add timing information
          if (duration) {
            scope.setContext('timing', {
              duration,
              startTime: config?.metadata?.startTime,
              endTime: Date.now(),
            });
          }

          // Set additional tags for filtering
          scope.setTag('api_error', true);
          scope.setTag('api_status', status || 'network_error');
          scope.setTag('api_method', error.config?.method || 'unknown');
          scope.setTag('api_endpoint', error.config?.url || 'unknown');

          // Capture the exception with all context
          Sentry.captureException(error);
        });
      }

      // Handle 422 business logic errors
      if (error.response?.status === 422) {
        handleBusinessErrorIfApplicable(error);
      }

      // Session expired / invalid Cognito token on staff app routes.
      // Skip public invite/magic-link flows and 403 (permission, not auth expiry).
      if (status === 401 && !isPublicAuthPath()) {
        handleAuthFailure('API returned 401');
      } else if (status === 403) {
        console.warn('Forbidden access attempt');
      }
      
      return Promise.reject(error);
    }
  );
  
  return apiClient;
}; 